// hooks/useApiData.js
import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { API_URL } from '../constants/data';


export const useApiData = (defaultStateData = null) => {
    const [totalCounts, setTotalCounts] = useState(0);
    const [isLoading, setIsLoading] = useState(false);


    const getAccessToken = () => {
        return localStorage.getItem("access_token") || "";
    }

    const fetchCategories = useCallback(async (prompt, setCategories) => {
        try {
            const data = await apiService.fetchCategories(prompt);
            setCategories(data);
            return data;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    }, []);

    const fetchSemanticRCA = useCallback(async (filters) => {
        try {
            const params = {
                query: filters.query,
                startDate: filters.startDate?.toISOString().split('T')[0],
                endDate: filters.endDate?.toISOString().split('T')[0],
                threshold: filters.threshold,
                value: filters.value,
                complaintNumbers: filters.complaintNumbers
            };
            const data = await apiService.fetchSemanticRCA(params);
            setTotalCounts(data.total_counts);
            return data.complaintNumbers;
        } catch (error) {
            console.error('Error fetching semantic RCA:', error);
            return [];
        }
    }, []);

    const fetchSemanticRCA2 = useCallback(async (filters) => {
        try {
            const params = {
                query: filters.query,
                startDate: filters.startDate?.toISOString().split('T')[0],
                endDate: filters.endDate?.toISOString().split('T')[0],
                threshold: filters.threshold,
                value: filters.value,
                complaintNumbers: filters.complaintNumbers
            };
            const data = await apiService.fetchSemanticRCA(params);
            // setTotalCounts(data.total_counts);
            return data;
        } catch (error) {
            console.error('Error fetching semantic RCA:', error);
            return [];
        }
    }, []);

    const fetchUserData = useCallback(async (params, setUserData) => {
        try {
            const data = await apiService.fetchUserData(params);
            if (setUserData) setUserData(data);
            return data;
        } catch (error) {
            console.error('Error fetching user data:', error);
            if (setUserData) setUserData([]);
            return [];
        }
    }, []);

    const formatStateName = (name) => {
        if (!name) return name;
        return name
            .toLowerCase()
            .replace(/&/g, "and")
            .replace(/\s+/g, " ")
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };


    const fetchSpatialData = useCallback(async (filters, setStateData) => {
        try {
            const params = {
                query: filters.query,
                startDate: filters.startDate,
                endDate: filters.endDate,
                threshold: filters.threshold,
                value: filters.value,
                complaintNumbers: filters.complaintNumbers
            };

            const data = await apiService.fetchSpatialData(params);


            setStateData(data);
            return data;
        } catch (error) {
            console.error("Error fetching spatial data:", error);
            return []
        }
    }, []);


    const fetchCategoryWithCount = useCallback(async (categoryData, setCategories, filters) => {
        try {
            const data = await apiService.fetchCategoryWithCount(categoryData, filters);
            console.log("data from subcount", data)
            if (setCategories) setCategories(data?.updatedData || data);
            if (setTotalCounts) setTotalCounts(data?.totalCount)
            return data;
        } catch (error) {
            throw error;
        }
    }, [])

    const fetchSectorDistribution = useCallback(async (filters) => {
        try {
            const data = await apiService.fetchSectorDsitribution(filters);
            return data;
        } catch (error) {
            throw error;
        }
    }, []);

    const fetchMultipleDistributions = useCallback(
        async (attributes, baseFilters, setDistributionData, setLoading, setErrors) => {
            console.log("baseFilter", baseFilters)
            try {
                const fetchPromises = attributes.map(async (attribute) => {
                    setLoading((prev) => ({ ...prev, [attribute]: true }));
                    setErrors((prev) => ({ ...prev, [attribute]: null }));

                    const startDate = baseFilters.startDate
                        ? new Date(baseFilters.startDate).toISOString().split('T')[0]
                        : new Date().toISOString().split('T')[0];

                    const endDate = baseFilters.endDate
                        ? new Date(baseFilters.endDate).toISOString().split('T')[0]
                        : new Date().toISOString().split('T')[0];

                    try {
                        const data = await apiService.fetchFeedbackDistribution({
                            attribute,
                            Sector: baseFilters.Sector,
                            CompanyName: baseFilters.CompanyName,
                            Category: baseFilters.Category,
                            skip: baseFilters.skip,
                            limit: baseFilters.limit,
                            startDate: startDate,
                            endDate: endDate,
                        });

                        let distributionList = Array.isArray(data?.distribution) ? data.distribution : (data?.distribution?.distribution || []);
                        const complaintsCount = distributionList.reduce((sum, item) => sum + (item?.count || 0), 0);

                        if (attribute !== 'userexperience') {
                            const totalComplaintsKey = `${attribute}ComplaintsCount`
                            setDistributionData((prev) => ({
                                ...prev,
                                [attribute]: distributionList,
                                [totalComplaintsKey]: complaintsCount
                            }));
                        }
                        if (attribute === 'userexperience') {
                            distributionList = distributionList.filter(
                                (item) =>
                                    item?.attribute_value !== null &&
                                    item?.attribute_value !== undefined &&
                                    item?.attribute_value !== ""
                            );
                            const totalCount = distributionList.reduce(
                                (sum, item) => sum + (item?.count || 0),
                                0
                            );
                            const countKey = `${attribute}Count`;

                            setDistributionData((prev) => ({
                                ...prev,
                                [attribute]: distributionList,
                                [countKey]: totalCount,
                            }));
                        }

                        return { attribute, success: true };
                    } catch (error) {
                        setErrors((prev) => ({
                            ...prev,
                            [attribute]: error.message,
                        }));
                        return { attribute, success: false, error };
                    } finally {
                        setLoading((prev) => ({ ...prev, [attribute]: false }));
                    }
                });

                const results = await Promise.all(fetchPromises);
                return results;
            } catch (error) {
                throw error;
            }
        },
        []
    );

    const fetchFeedbackStats = useCallback(async (filters, setFeedbackStats) => {
        try {
            const data = await apiService.fetchFeedbackStats(filters);
            setFeedbackStats(data);
            return data;
        } catch (error) {
            throw error;
        }
    }, []);


    const fetchAllCompanyDetails = useCallback(async (filters, setCompanyData) => {
        try {
            const data = await apiService.fetchCompanyDistribution(filters);
            setCompanyData(data);
            return data;
        } catch (error) {
            throw error;
        }
    }, [])

    const fetchAllTypeCompanyDetails = useCallback(async (filters, setCompanyData) => {
        try {
            const data = await apiService.fetchAllCompanyDistribution(filters);
            setCompanyData(data);
            return data;
        } catch (error) {
            throw error;
        }
    }, [])

    const fetchCategoryAlert = useCallback(async (filters, categories = null) => {
        try {
            const data = await apiService.fetchCategoryAlerts(filters, categories);
            return data;
        } catch (error) {
            console.error('Error fetching category alert:', error);
            throw error;
        }
    }, []);


    const fetchFeedbackKeyWords = useCallback(async (params) => {
        try {
            const data = await apiService.fetchFeedbackKeyWords(params);
            return data;
        } catch (error) {
            console.error('Error fetching feedback keywords:', error);
            throw error;
        }
    }, []);

    const fetchFeedbackByRating = useCallback(async (params) => {
        try {
            const data = await apiService.fetchFeedbackByRating(params);
            return data;
        } catch (error) {
            console.error('Error fetching feedback by rating:', error);
            throw error;
        }
    }, []);

    return {
        totalCounts,
        setTotalCounts,
        // userData,
        // setStateData,
        // stateData,
        // companyData,
        // setCompanyData,
        // setUserData,
        isLoading,
        setIsLoading,
        fetchCategories,
        fetchSemanticRCA,
        fetchSemanticRCA2,
        fetchSpatialData,
        fetchCategoryWithCount,
        fetchUserData,
        fetchSectorDistribution,
        fetchMultipleDistributions,
        fetchAllCompanyDetails,
        fetchFeedbackKeyWords,
        fetchCategoryAlert,
        fetchFeedbackStats,
        fetchFeedbackByRating,
        fetchAllTypeCompanyDetails
    };
};